import { inject, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Route,
  RouterModule,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { authGuard } from '@deltager/organizers/admin/core/auth/auth-guard.service';
import { UserDataResolver } from './resolvers/user-data.resolver';
import { ApplicationErrorComponent } from '@deltager/organizers/admin/shared/common-ui-components/application-error/application-error.component';
import { CanDeactivateGuard } from '@deltager/organizers/admin/modules/events/create-event/can-deactivate.guard';
import { HardLogoutGuardService } from '../auth/hard-logout-guard.service';
import { CancellationGuardService } from '../auth/cancellation-guard.service';
import { DiscountsResolver } from './resolvers/discounts.resolver';
import { AdminGuardService } from '../services/admin-guard.service';
import { PostOnboardingLandingPageComponent } from '../../shared/components/post-onboarding-landing-page/post-onboarding-landing-page.component';
import { OrganizerSettingsResolver } from '@deltager/organizers/admin/core/app-routing/resolvers/organizer-settings.resolver';
import { ResolverKeys } from '@deltager/organizers/admin/core/app-routing/resolvers/resolver-keys.constants';
import { REPORTING_CHILDREN_ROUTES } from '@deltager/organizers/admin/core/app-routing/reporting-routes.constants';
import { BOOKING_REPORT_TYPE_ROUTES } from '@deltager/organizers/admin/core/app-routing/booking-report-routes.constants';

const adminGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
  inject(AdminGuardService).canActivate(route, state);

const cancellationGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
  inject(CancellationGuardService).canActivate(route, state);

export type TypedPathRoute<PathTuple extends readonly string[]> = Array<Route & { path: PathTuple[number] }>;

const routes: Routes = [
  { path: '', redirectTo: '/events', pathMatch: 'full' },
  { path: 'post-onboarding', component: PostOnboardingLandingPageComponent },
  {
    path: 'login',
    loadChildren: () => import('@deltager/organizers/admin/modules/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'consents',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/organizers/user-consents/user-consents.module').then(
        m => m.UserConsentsModule,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'organizers',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/organizers/organizers.module').then(m => m.OrganizersModule),
    canActivate: [authGuard],
    canActivateChild: [authGuard],
  },
  {
    path: 'signup',
    loadChildren: () => import('@deltager/organizers/admin/modules/signup/signup.routing').then(m => m.SignupRoutes),
  },
  {
    path: 'user',
    loadChildren: () => import('@deltager/organizers/admin/modules/user/user.module').then(m => m.UserModule),
  },
  {
    path: 'events/new',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/events/new-event/new-event.module').then(m => m.NewEventModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
    data: {
      isCreateEvent: true,
    },
  },
  {
    path: 'events/create',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/events/create-event/create-event.module').then(
        m => m.CreateEventModule,
      ),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
    data: {
      isCreateEvent: true,
    },
  },
  {
    path: 'events/edit',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/events/create-event/create-event.module').then(
        m => m.CreateEventModule,
      ),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'events/:eventId/waiting-list',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/waiting-list/waiting-list.module').then(m => m.WaitingListModule),
  },
  {
    path: 'events/:eventId/communication',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/communications-invitations/communications-invitations.module').then(
        m => m.CommunicationsInvitationsModule,
      ),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'haf-report',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/haf-report/haf-report.module').then(m => m.HAFReportModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
    data: {
      isLimitedAccess: true,
    },
  },
  {
    path: 'tags',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/tags/tags-routing.module').then(m => m.TagsRoutingModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'levels',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/level/level-routing.module').then(m => m.LevelRoutingModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'types',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/type/type-routing.module').then(m => m.TypeRoutingModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'instructors',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/instructor/instructor-routing.module').then(
        m => m.InstructorRoutingModule,
      ),
  },
  {
    path: 'venues',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/venue/venue-routing.module').then(m => m.VenueRoutingModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'cancel',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/cancellation/cancellation.module').then(m => m.CancellationModule),
    canActivate: [authGuard, cancellationGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },
  {
    path: 'events/:eventId/participants',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/organizers/participants-list/participants-list.module').then(
        m => m.ParticipantsListModule,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'events/:eventId/administration',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/events/administration/administration.module').then(
        m => m.AdministrationModule,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'events',
    canActivate: [authGuard],
    loadComponent: () =>
      import('@deltager/organizers/admin/modules/events/events-list-main/events-list-main.component').then(
        m => m.EventsListMainComponent,
      ),
    resolve: {
      userData: UserDataResolver,
      organizerSettings: OrganizerSettingsResolver,
    },
  },
  {
    path: 'elearning',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/e-learning/e-learning.module').then(m => m.ELearningModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
    data: {
      isLimitedAccess: true,
    },
  },
  {
    path: 'booking-events',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/booking-event-setup/booking-event-setup.module').then(
        m => m.BookingEventSetupModule,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'booking-events/:eventId/participant-list',
    canActivate: [authGuard],
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/booking-participant-list/booking-participant-list.module').then(
        m => m.BookingParticipantListModule,
      ),
  },
  {
    path: 'home',
    pathMatch: 'full',
    loadComponent: () =>
      import('@deltager/organizers/admin/modules/home/home-blank/home-blank.component').then(m => m.HomeBlankComponent),
  },

  {
    path: 'orders',
    loadChildren: () => import('@deltager/organizers/admin/modules/orders/orders.module').then(m => m.OrdersModule),
  },
  /*   {
    path: "orders/:orderId/orderdetails/:orderDetailId/sessions",
    component: OrderSessionSearchComponent,
    pathMatch: "full"
  }, */
  {
    path: 'invoices',
    loadChildren: () => import('@deltager/organizers/admin/modules/invoice/invoice.module').then(m => m.InvoiceModule),
    resolve: {
      userData: UserDataResolver,
    },
    data: {
      isLimitedAccess: true,
    },
  },

  {
    path: 'content',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/home/home-routing.module').then(m => m.HomeRoutingModule),
  },

  {
    path: 'viewssync',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/viewsSync/viewsSync.module').then(m => m.ViewsSyncModule),
    resolve: {
      userData: UserDataResolver,
    },
    data: {
      isLimitedAccess: true,
    },
  },
  {
    path: 'register',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/registration/registration.module').then(m => m.RegistrationModule),
  },

  {
    path: 'discounts',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/discounts/discounts-routing.module').then(
        m => m.DiscountsRoutingModule,
      ),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
      DiscountsResolver,
    },
  },

  {
    path: 'booking-report',
    loadComponent: () =>
      import('@deltager/organizers/admin/modules/booking-report/booking-report.component').then(
        m => m.BookingReportComponent,
      ),
    children: BOOKING_REPORT_TYPE_ROUTES,
    canActivate: [authGuard],
    resolve: {
      [ResolverKeys.UserData]: UserDataResolver,
    },
  },

  {
    path: 'credits',
    loadChildren: () =>
      import('@deltager/organizers/admin/modules/credits/credits-routing.module').then(m => m.CreditsRoutingModule),
    canActivate: [authGuard],
    resolve: {
      userData: UserDataResolver,
    },
  },

  {
    path: 'reporting',
    loadComponent: () =>
      import('@deltager/organizers/admin/modules/reporting/reporting-main/reporting-main.component').then(
        m => m.ReportingMainComponent,
      ),
    children: REPORTING_CHILDREN_ROUTES,
    canActivate: [authGuard, adminGuard],
    resolve: {
      [ResolverKeys.UserData]: UserDataResolver,
      [ResolverKeys.OrganizerSettings]: OrganizerSettingsResolver,
    },
  },

  {
    path: 'hard-logout',
    canActivate: [HardLogoutGuardService],
    children: [{ path: '', pathMatch: 'full', redirectTo: '/login' }],
  },

  { path: 'error', component: ApplicationErrorComponent },
  { path: 'page-not-found', component: PageNotFoundComponent },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      enableTracing: false,
      initialNavigation: 'disabled',
    }),
  ],
  exports: [RouterModule],
  declarations: [PageNotFoundComponent],
  providers: [UserDataResolver, CanDeactivateGuard, DiscountsResolver],
})
export class AppRoutingModule {}
